import { useState, useCallback } from 'react';
import { DEBUG_ENDPOINTS } from '../constants/formConstants';
import useAuth from './useAuth';

/**
 * Initial state for all debug info data
 */
const INITIAL_DEBUG_DATA = {
  case: {},
  patient: {},
  provider: {},
  organization: {},
  organizationGroup: {},
  outcomePlan: {},
  taskGraphTemplates: {},
  outcomePlanInstance: {},
  taskGraphInstances: {},
  nextExecutionTime: {},
  executions: {},
  schedulerStatus: {},
  interactionRequests: {},
  interactionResponses: {},
};

/**
 * Custom hook for fetching case debug information
 * Fetches data from multiple endpoints in parallel
 */
const useCaseDebugInfo = () => {
  const { acquireToken } = useAuth();

  // Debug data state
  const [debugData, setDebugData] = useState(INITIAL_DEBUG_DATA);

  // Fetch state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchedCaseId, setFetchedCaseId] = useState('');

  /**
   * Fetches debug info for a case from all endpoints
   * @param {string} caseId - The case ID to fetch info for
   * @returns {Promise<object>} All debug data
   */
  const fetchDebugInfo = useCallback(
    async (caseId) => {
      if (!caseId) {
        throw new Error('Case ID is required');
      }

      setIsLoading(true);
      setError(null);

      try {
        const accessToken = await acquireToken();
        const baseUrl = process.env.REACT_APP_JARVIS_BASE_URL + caseId;

        const headers = {
          accept: '*/*',
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${accessToken}`,
        };

        // Build all endpoint URLs
        const endpoints = {
          case: baseUrl + DEBUG_ENDPOINTS.case,
          patient: baseUrl + DEBUG_ENDPOINTS.patient,
          provider: baseUrl + DEBUG_ENDPOINTS.provider,
          organization: baseUrl + DEBUG_ENDPOINTS.organization,
          organizationGroup: baseUrl + DEBUG_ENDPOINTS.organizationGroup,
          outcomePlan: baseUrl + DEBUG_ENDPOINTS.outcomePlan,
          taskGraphTemplates: baseUrl + DEBUG_ENDPOINTS.taskGraphTemplates,
          outcomePlanInstance: baseUrl + DEBUG_ENDPOINTS.outcomePlanInstance,
          taskGraphInstances: baseUrl + DEBUG_ENDPOINTS.taskGraphInstances,
          nextExecutionTime: baseUrl + DEBUG_ENDPOINTS.nextExecutionTime,
          executions: baseUrl + DEBUG_ENDPOINTS.executions,
          schedulerStatus: baseUrl + DEBUG_ENDPOINTS.schedulerStatus,
          interactionRequests: baseUrl + DEBUG_ENDPOINTS.interactionRequests,
          interactionResponses: baseUrl + DEBUG_ENDPOINTS.interactionResponses,
        };

        // Fetch all endpoints in parallel
        const fetchPromises = Object.entries(endpoints).map(async ([key, url]) => {
          try {
            const response = await fetch(url, { method: 'GET', headers });
            if (response.ok) {
              const data = await response.json();
              return [key, data];
            } else {
              console.error(`Error fetching ${key}:`, response.statusText);
              return [key, { error: response.statusText }];
            }
          } catch (err) {
            console.error(`Error fetching ${key}:`, err);
            return [key, { error: err.message }];
          }
        });

        const results = await Promise.all(fetchPromises);

        // Build debug data object from results
        const newDebugData = results.reduce((acc, [key, data]) => {
          acc[key] = data;
          return acc;
        }, {});

        setDebugData(newDebugData);
        setFetchedCaseId(caseId);

        return newDebugData;
      } catch (err) {
        console.error('Error fetching debug info:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [acquireToken]
  );

  /**
   * Reset debug info state
   */
  const reset = useCallback(() => {
    setDebugData(INITIAL_DEBUG_DATA);
    setFetchedCaseId('');
    setError(null);
  }, []);

  /**
   * Check if debug data has been loaded
   */
  const hasData = fetchedCaseId !== '';

  return {
    // Data
    debugData,
    fetchedCaseId,

    // Individual data accessors for convenience
    caseData: debugData.case,
    patientData: debugData.patient,
    providerData: debugData.provider,
    organizationData: debugData.organization,
    organizationGroupData: debugData.organizationGroup,
    outcomePlanData: debugData.outcomePlan,
    taskGraphTemplatesData: debugData.taskGraphTemplates,
    outcomePlanInstanceData: debugData.outcomePlanInstance,
    taskGraphInstancesData: debugData.taskGraphInstances,
    nextExecutionTimeData: debugData.nextExecutionTime,
    executionsData: debugData.executions,
    schedulerStatusData: debugData.schedulerStatus,
    interactionRequestsData: debugData.interactionRequests,
    interactionResponsesData: debugData.interactionResponses,

    // State
    isLoading,
    error,
    hasData,

    // Actions
    fetchDebugInfo,
    reset,
  };
};

export default useCaseDebugInfo;
